import { type ReactNode } from 'react'
import invariant from 'tiny-invariant'
import { fbt } from 'fbtee'
import { MapPin, Calendar } from 'lucide-react'
import { Card } from './ui/card'
import { Skeleton } from './ui/skeleton'
import { MemberFacepile } from './MemberFacepile'
import { cn, getGradientPair } from '../lib/utils'
import { formatEventDateRange, eventCountdown } from '../lib/formatDate'
import { useForceLoading } from '../lib/skeletonDebug'
import type { EventCardEvent } from './EventCard'
import { CancelledBadge } from './CancelledBadge'
import { EventCardLineup } from './EventCardLineup'
import type { UserProfile } from '../types'
import type { TicketMarks } from '../hooks/useTicketMarksByEvent'
import { clickableProps } from '../lib/a11y'
import { focalPosition } from '../lib/focalPosition'

const EMPTY_GOING_SET: Set<string> = new Set()

interface Props {
  loading?: boolean
  facepileLoading?: boolean
  event?: EventCardEvent
  today?: string
  onClick?: () => void
  facepile?: { userIds: string[]; goingUserIds: Set<string>; pregameUserIds?: ReadonlySet<string>; tickets?: TicketMarks }
  profiles?: Record<string, UserProfile>
  currentUserId?: string | null
  locale?: string
  action?: ReactNode
  /** Lineup names the title doesn't already spell out (see getLineupNames). */
  lineup?: string[]
  /** The viewer's followed artists on the lineup — lead the subtitle, highlighted. */
  followedArtists?: string[]
  /** Whole lineup's length, for "+N more" when `lineup` isn't loaded. */
  lineupSize?: number
  /** Under the event's details, e.g. its pre-game. */
  footer?: ReactNode
}

function CountdownLabel({ event, today }: { event: EventCardEvent; today: string }) {
  const countdown = eventCountdown(event.startDate, event.endDate, today)
  if (countdown.status === 'now')
    return fbt('Happening now', 'Countdown chip on the hero card for an event happening now')
  if (countdown.status === 'today')
    return <fbt desc="Countdown chip on the hero card for an event starting today">Today</fbt>
  if (countdown.days === 1)
    return <fbt desc="Countdown chip on the hero card for an event starting tomorrow">Tomorrow</fbt>
  return (
    <fbt desc="Countdown chip on the hero card counting days until the event">
      In <fbt:plural count={countdown.days} showCount="yes" name="days" many="days">
        day
      </fbt:plural>
    </fbt>
  )
}

export function HeroEventCard({
  loading: loadingProp,
  facepileLoading,
  event,
  today,
  onClick,
  facepile,
  profiles = {},
  currentUserId,
  locale,
  action,
  lineup = [],
  followedArtists = [],
  lineupSize,
  footer,
}: Props) {
  const loading = useForceLoading(loadingProp)
  if (loading) {
    return (
      <Card className="relative flex min-h-56 flex-col justify-between overflow-hidden p-4 bg-muted">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-8 w-3/4" />
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <Skeleton className="h-3.5 w-1/2" />
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <Skeleton className="h-3.5 w-1/3" />
            </div>
          </div>
        </div>
      </Card>
    )
  }

  invariant(event, 'HeroEventCard: event is required when not loading')
  invariant(today, 'HeroEventCard: today is required when not loading')
  invariant(onClick, 'HeroEventCard: onClick is required when not loading')
  invariant(locale, 'HeroEventCard: locale is required when not loading')

  const dateRange = formatEventDateRange(event.startDate, event.endDate, locale)
  const facepileUserIds = facepile?.userIds ?? []
  const facepileGoing = facepile?.goingUserIds ?? EMPTY_GOING_SET
  const goingMemberIds = facepileUserIds.filter(id => facepileGoing.has(id))
  const interestedMemberIds = facepileUserIds.filter(id => !facepileGoing.has(id))

  let style: React.CSSProperties
  if (event.imageUrl) {
    style = {
      backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.45)), url(${event.imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: focalPosition(event, 'center'),
    }
  } else {
    const [c1, c2] = getGradientPair(event.id)
    style = {
      backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.65)), linear-gradient(135deg, ${c1}, ${c2})`,
    }
  }

  return (
    <Card
      style={style}
      aria-label={event.name}
      {...clickableProps(e => {
        if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return
        onClick()
      })}
      className={cn(
        'relative flex min-h-56 flex-col justify-between overflow-hidden p-4 cursor-pointer transition-colors',
        'border-primary/30 hover:border-primary/50',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {event.cancelled ? (
          <CancelledBadge className="px-3 py-1 shadow-sm" reason={event.cancelledReason} />
        ) : (
          <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
            <CountdownLabel event={event} today={today} />
          </span>
        )}
        {action}
      </div>

      <div className="space-y-2">
        <h2
          className={cn(
            'text-2xl font-bold leading-tight text-on-image drop-shadow-sm',
            event.cancelled && 'line-through opacity-70',
          )}
        >
          {event.name}
        </h2>
        {(lineup.length > 0 || followedArtists.length > 0) && (
          <EventCardLineup lineup={lineup} followed={followedArtists} lineupSize={lineupSize} eventName={event.name} className="-mt-1 text-sm" />
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-1.5 text-sm text-on-image/85">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{event.venueName ?? event.location}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-on-image/85">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span>{dateRange}</span>
            </div>
          </div>
          <MemberFacepile
            loading={facepileLoading}
            interestedMemberIds={interestedMemberIds}
            goingMemberIds={goingMemberIds}
            profiles={profiles}
            currentUserId={currentUserId}
            dialogTitle={event.name}
            historyKey={`hero:${event.id}`}
            pregameGoerIds={facepile?.pregameUserIds}
            tickets={facepile?.tickets}
          />
        </div>
        {footer}
      </div>
    </Card>
  )
}
